import { ChevronRight } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import { TabStatusIndicator } from '@/shared/components/status';
import { Button } from '@/shared/components/ui';
import type { StatusOption, StatusValue } from '@/shared/schema';
import { cn } from '@/shared/utils';

export type ObjectSidemenuItem = {
  id: string;
  label: string;
  kind: 'master-data' | 'files' | 'category-group' | 'custom-group';
  statusIndicator?: {
    statusValue: StatusValue | undefined | null;
    statusOptions?: StatusOption[];
    icon?: string;
  } | null;
};

type ObjectMetadataSidemenuProps = {
  items: ObjectSidemenuItem[];
  activeId: string | null;
  onSelect: (id: string) => void;
  showCustomSeparator?: boolean;
};

export function ObjectMetadataSidemenu({
  items,
  activeId,
  onSelect,
  showCustomSeparator,
}: ObjectMetadataSidemenuProps) {
  const { t } = useIntl();

  const { masterData, categoryGroups, customGroups } = useMemo(() => {
    const masterData = items.filter((i) => i.kind === 'master-data' || i.kind === 'files');
    const categoryGroups = items.filter((i) => i.kind === 'category-group');
    const customGroups = items.filter((i) => i.kind === 'custom-group');
    return { masterData, categoryGroups, customGroups };
  }, [items]);

  const renderItem = (item: ObjectSidemenuItem) => {
    const isActive = activeId === item.id;
    return (
      <Button
        key={item.id}
        type="button"
        variant="ghost"
        className={cn(
          'w-full justify-between px-3 py-2 h-auto text-sm font-medium rounded-lg',
          isActive
            ? 'bg-muted text-foreground hover:bg-muted'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
        )}
        onClick={() => onSelect(item.id)}
      >
        <span className="flex items-center gap-2 min-w-0">
          {item.statusIndicator && (
            <TabStatusIndicator
              statusValue={item.statusIndicator.statusValue}
              statusOptions={item.statusIndicator.statusOptions}
              icon={item.statusIndicator.icon}
            />
          )}
          <span className="truncate">{item.label}</span>
        </span>
        <ChevronRight className={cn('size-4 shrink-0', isActive ? 'opacity-100' : 'opacity-40')} />
      </Button>
    );
  };

  return (
    <nav className="space-y-2">
      <div className="space-y-1">{masterData.map(renderItem)}</div>

      {categoryGroups.length > 0 && (
        <div className="space-y-1">{categoryGroups.map(renderItem)}</div>
      )}

      {showCustomSeparator && customGroups.length > 0 && (
        <div className="pt-3">
          <div className="h-px bg-border mb-6" />
          <div className="px-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {t('metadata.customMetadata')}
          </div>
        </div>
      )}

      {customGroups.length > 0 && <div className="space-y-1">{customGroups.map(renderItem)}</div>}
    </nav>
  );
}
